import React, { useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, Phone, Bot, AlertCircle, ArrowRight, ArrowLeft } from "lucide-react";
import { useDemoForm } from "@/hooks/useDemoForm";
import { useNavigate } from "react-router-dom";

const ACTIVITY_DOMAINS = [
  "Plomberie",
  "Electricite",
  "Serrurerie",
  "Chauffage / Climatisation",
  "Menuiserie",
  "Peinture",
  "Maconnerie",
  "Carrelage",
  "Toiture / Couverture",
  "Renovation",
  "Entreprise generale",
  "Depannage multi-services",
  "Autre",
];

export const DemoFormPopup = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const {
    step,
    formData,
    errors,
    generalError,
    isSubmitting,
    isSuccess,
    handleChange,
    submitStep1,
    submitStep2,
    goBack,
    resetForm,
    getInputClass,
  } = useDemoForm();

  useEffect(() => {
    if (isSuccess) {
      resetForm();
      onClose();
      navigate("/confirmation");
    }
  }, [isSuccess, resetForm, onClose, navigate]);

  const handleClose = () => {
    if (!isSubmitting) {
      resetForm();
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent
        data-testid="demo-form-popup"
        className="sm:max-w-[500px] max-h-[calc(100dvh-16px)] sm:max-h-[calc(100vh-40px)] flex flex-col rounded-md border-2 border-slate-200 p-0 overflow-hidden"
        style={{ boxShadow: "6px 6px 0px rgba(30, 90, 158, 0.15)" }}
      >
        {/* Header */}
        <div className="bg-primary px-4 sm:px-6 py-4 sm:py-5 flex items-center gap-3 sm:gap-4 rounded-t-md flex-shrink-0">
          <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-md bg-white/10 flex items-center justify-center flex-shrink-0">
            <Bot className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
          </div>
          <div className="flex-1">
            <DialogTitle className="text-base sm:text-lg font-black text-white" style={{ fontFamily: "Barlow, sans-serif" }}>
              Reservez votre demo
            </DialogTitle>
            <DialogDescription className="text-blue-200 text-xs sm:text-sm">
              Etape {step}/2 — {step === 1 ? "Vos coordonnees" : "Votre entreprise"}
            </DialogDescription>
          </div>
          {/* Step dots */}
          <div className="flex gap-1.5">
            <div className={`w-2.5 h-2.5 rounded-full ${step >= 1 ? "bg-accent" : "bg-white/30"}`} />
            <div className={`w-2.5 h-2.5 rounded-full ${step >= 2 ? "bg-accent" : "bg-white/30"}`} />
          </div>
        </div>

        {/* Scrollable form area */}
        <div className="flex-1 overflow-y-auto overscroll-contain px-3 sm:px-5 md:px-6 pb-4 sm:pb-6 pt-3 sm:pt-4" style={{ WebkitOverflowScrolling: "touch" }}>
          {generalError && <ErrorBanner message={generalError} />}

          {step === 1 ? (
            <form onSubmit={submitStep1} className="space-y-3 sm:space-y-4" data-testid="demo-step1">
              <FieldRow>
                <TextField id="firstName" label="Prenom" placeholder="Jean" formData={formData} errors={errors} onChange={handleChange} getInputClass={getInputClass} />
                <TextField id="lastName" label="Nom" placeholder="Dupont" formData={formData} errors={errors} onChange={handleChange} getInputClass={getInputClass} />
              </FieldRow>
              <TextField id="email" label="Email" placeholder="jean@exemple.ch" type="email" formData={formData} errors={errors} onChange={handleChange} getInputClass={getInputClass} />
              <PhoneField formData={formData} errors={errors} onChange={handleChange} getInputClass={getInputClass} />
              <Button
                type="submit"
                data-testid="demo-step1-next"
                className="w-full bg-accent hover:bg-orange-600 text-white py-5 sm:py-6 text-sm sm:text-base font-bold rounded-md transition-all hover:scale-[1.02] hover:shadow-lg mt-2"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <><Loader2 className="mr-2 w-5 h-5 animate-spin" />Enregistrement...</>
                ) : (
                  <>Suivant <ArrowRight className="ml-2 w-5 h-5" /></>
                )}
              </Button>
              <p className="text-xs text-center text-slate-400">
                Vos donnees sont sauvegardees de maniere securisee.
              </p>
            </form>
          ) : (
            <form onSubmit={submitStep2} className="space-y-3 sm:space-y-4" data-testid="demo-step2">
              <TextField id="companyName" label="Nom de l'entreprise" placeholder="Dupont Plomberie Sarl" formData={formData} errors={errors} onChange={handleChange} getInputClass={getInputClass} />
              <DomainSelect value={formData.activityDomain} error={errors.activityDomain} onChange={(v) => handleChange("activityDomain", v)} getInputClass={getInputClass} />
              <TextField id="address" label="Adresse" placeholder="Rue de la Gare 15" formData={formData} errors={errors} onChange={handleChange} getInputClass={getInputClass} />
              <FieldRow>
                <TextField id="postalCode" label="Code postal" placeholder="1000" formData={formData} errors={errors} onChange={handleChange} getInputClass={getInputClass} testId="demo-postal" />
                <TextField id="city" label="Ville" placeholder="Lausanne" formData={formData} errors={errors} onChange={handleChange} getInputClass={getInputClass} />
              </FieldRow>
              <div className="flex flex-col-reverse sm:flex-row gap-2 mt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={goBack}
                  data-testid="demo-step2-back"
                  className="border-2 border-slate-300 text-slate-600 py-4 sm:py-5 font-semibold rounded-md w-full sm:w-auto"
                >
                  <ArrowLeft className="mr-1 w-4 h-4" />
                  Retour
                </Button>
                <Button
                  type="submit"
                  data-testid="demo-submit"
                  className="flex-1 bg-accent hover:bg-orange-600 text-white py-5 sm:py-6 text-sm sm:text-base font-bold rounded-md transition-all hover:scale-[1.02] hover:shadow-lg w-full"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <><Loader2 className="mr-2 w-5 h-5 animate-spin" />Envoi en cours...</>
                  ) : (
                    <><Phone className="mr-2 w-5 h-5" />Recevoir un appel de demo</>
                  )}
                </Button>
              </div>
              <p className="text-xs text-center text-slate-400">
                En soumettant ce formulaire, vous acceptez d'etre contacte par notre equipe.
              </p>
            </form>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

/* ---- Sub-components ---- */

const ErrorBanner = ({ message }) => (
  <div className="flex items-center gap-2 p-3 bg-accent/10 border border-accent/30 rounded-md text-accent text-sm mb-4" data-testid="demo-form-error">
    <AlertCircle className="w-4 h-4 flex-shrink-0" />
    <span className="font-medium">{message}</span>
  </div>
);

const FieldRow = ({ children }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">{children}</div>
);

const TEST_ID_MAP = {
  firstName: "demo-first-name",
  lastName: "demo-last-name",
  companyName: "demo-company",
  email: "demo-email",
  phone: "demo-phone",
  address: "demo-address",
  postalCode: "demo-postal",
  city: "demo-city",
};

const COUNTRIES = [
  { code: "CH", prefix: "+41", flag: "🇨🇭", placeholder: "79 123 45 67" },
  { code: "FR", prefix: "+33", flag: "🇫🇷", placeholder: "6 12 34 56 78" },
];

const PhoneField = ({ formData, errors, onChange, getInputClass }) => {
  const country = COUNTRIES.find((c) => c.prefix === formData.phonePrefix) || COUNTRIES[0];
  return (
    <div className="space-y-1">
      <Label htmlFor="phone" className="text-xs sm:text-sm font-semibold text-slate-700 flex items-center gap-1">
        Téléphone <span className="text-accent">*</span>
        {errors.phone && <span className="text-accent text-xs ml-auto font-medium">{errors.phone}</span>}
      </Label>
      <div className="flex gap-1.5">
        <Select value={formData.phonePrefix} onValueChange={(v) => onChange("phonePrefix", v)}>
          <SelectTrigger data-testid="demo-phone-prefix" className={`h-9 sm:h-10 w-[90px] sm:w-[100px] text-sm border-2 flex-shrink-0 ${getInputClass("phone")}`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {COUNTRIES.map((c) => (
              <SelectItem key={c.code} value={c.prefix}>
                <span className="flex items-center gap-1.5">
                  <span>{c.flag}</span>
                  <span className="font-medium">{c.prefix}</span>
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Input
          id="phone"
          data-testid="demo-phone"
          type="tel"
          placeholder={country.placeholder}
          value={formData.phone}
          onChange={(e) => onChange("phone", e.target.value)}
          className={`h-9 sm:h-10 text-sm border-2 flex-1 ${getInputClass("phone")}`}
        />
      </div>
    </div>
  );
};

const TextField = ({ id, label, placeholder, type = "text", formData, errors, onChange, getInputClass, testId }) => (
  <div className="space-y-1">
    <Label htmlFor={id} className="text-xs sm:text-sm font-semibold text-slate-700 flex items-center gap-1">
      {label} <span className="text-accent">*</span>
      {errors[id] && <span className="text-accent text-xs ml-auto font-medium">{errors[id]}</span>}
    </Label>
    <Input
      id={id}
      data-testid={testId || TEST_ID_MAP[id]}
      type={type}
      placeholder={placeholder}
      value={formData[id]}
      onChange={(e) => onChange(id, e.target.value)}
      className={`h-9 sm:h-10 text-sm border-2 ${getInputClass(id)}`}
    />
  </div>
);

const DomainSelect = ({ value, error, onChange, getInputClass }) => (
  <div className="space-y-1">
    <Label htmlFor="activityDomain" className="text-xs sm:text-sm font-semibold text-slate-700 flex items-center gap-1">
      Domaine d'activite <span className="text-accent">*</span>
      {error && <span className="text-accent text-xs ml-auto font-medium">{error}</span>}
    </Label>
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid="demo-domain" className={`h-9 sm:h-10 text-sm border-2 ${getInputClass("activityDomain")}`}>
        <SelectValue placeholder="Selectionnez votre domaine" />
      </SelectTrigger>
      <SelectContent>
        {ACTIVITY_DOMAINS.map((domain) => (
          <SelectItem key={domain} value={domain}>{domain}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  </div>
);

export default DemoFormPopup;
